import { Dayjs } from 'dayjs';
import {
  Markierung,
  ArbeitszeitArbeitszeitantragStatus,
  ArbeitszeitenPauseneinstellung,
} from '../../../gql/graphql';
import { Abwesenheitsgrund } from './Abwesenheitsgrund';
import { ArbeitszeitAuditAenderungsTyp } from './ArbeitszeitAuditAenderungsTyp';
import { Termin } from './Termin';

export class Arbeitszeit {
  __typename: string;
  beginn2?: Zeitstempel;
  ende2?: Zeitstempel;
  kommen2?: Zeitstempel;
  gehen2?: Zeitstempel;
  constructor(
    readonly id: string,
    readonly mitarbeiter: Mitarbeiter,
    readonly arbeitsbereich: Arbeitsbereich,
    readonly abteilung: Abteilung,
    readonly dauer: number,
    readonly pausendauer: number,
    readonly puenktlichkeit: Puenktlichkeit,
    readonly pausen: Pause[],
    readonly pauseneinstellung: ArbeitszeitenPauseneinstellung,
    readonly dienstgaenge: Dienstgang[],
    readonly konflikte: Konflikt[],
    readonly rechtshinweise: Rechtshinweis[],
    readonly standortId: string,
    readonly start?: Date,
    readonly ende?: Date,
    readonly kommen?: Date,
    readonly gehen?: Date,
    readonly abwesenheit?: Abwesenheit,
    readonly bemerkung?: string,
    readonly zuschlaege?: Zuschlag[],
    readonly antraege?: Antrag[],
    readonly audit: Audit[] = [],
    readonly termin?: Termin,
    readonly geaendertVon?: string,
    readonly geaendertAm?: Date,
    readonly datum?: Date,
  ) {}
}

export class Schicht {
  constructor({
    id,
    beginn,
    ende,
  }: {
    id: string;
    beginn?: Dayjs;
    ende?: Dayjs;
  }) {
    this.id = id;
    this.beginn = beginn;
    this.ende = ende;
  }
  readonly id: string;
  readonly beginn: Dayjs;
  readonly ende: Dayjs;
}

export class Zeitstempel {
  constructor({
    abweichung,
    zeitpunkt,
    schicht,
  }: {
    abweichung: number;
    zeitpunkt: Date;
    schicht?: Schicht;
  }) {
    this.abweichung = abweichung;
    this.zeitpunkt = zeitpunkt;
    this.schicht = schicht;
  }
  readonly abweichung: number;
  readonly zeitpunkt: Date;
  readonly schicht?: Schicht;
}

export class Dienstplanuebernahme {
  constructor(
    readonly id: string,
    readonly schichtId: string,
    readonly beginn: Date,
    readonly pause: number,
    readonly dauer: number,
    readonly mitarbeiter: Mitarbeiter,
    readonly arbeitsbereich: Arbeitsbereich,
    readonly ende?: Date,
    readonly zuschlaege: Zuschlag[] = [],
    readonly rechtshinweise: Rechtshinweis[] = [],
    readonly bemerkung?: string,
  ) {}
}

export class Mitarbeiter {
  constructor(
    readonly vorname: string,
    readonly nachname: string,
    readonly id: string,
    readonly extern?: boolean,
    readonly bildUrl?: string,
    readonly arbeitsbereich?: Arbeitsbereich,
  ) {}
}

export class Arbeitsbereich {
  constructor(
    readonly name: string,
    readonly id: string,
    readonly markierung: Markierung,
    readonly abteilungen?: Abteilung[],
  ) {}
}

export class Abteilung {
  constructor(
    readonly name: string,
    readonly id: string,
    readonly markierung: Markierung,
  ) {}
}

export class Puenktlichkeit {
  constructor(
    readonly abweichungBeginn?: number,
    readonly abweichungEnde?: number,
  ) {}
}

export class Dienstgang {
  constructor(
    readonly id: string,
    readonly beginn: Date,
    readonly ende: Date,
    readonly dauer: number,
  ) {}
}

export class Pause {
  readonly id?: string;
  readonly beginn?: Date;
  readonly ende?: Date;
  readonly bezahlt: boolean;
  readonly art: ArbeitszeitenPausenart;
  readonly dauer?: number;

  constructor({
    id,
    dauer,
    art,
    beginn,
    ende,
    bezahlt,
  }: {
    id?: string;
    beginn?: Date;
    ende?: Date;
    bezahlt: boolean;
    art: ArbeitszeitenPausenart;
    dauer?: number;
  }) {
    this.dauer = dauer;
    this.id = id;
    this.beginn = beginn;
    this.ende = ende;
    this.bezahlt = bezahlt;
    this.art = art;
  }
}

export enum ArbeitszeitenPausenart {
  Automatisch = 'Automatisch',
  Dienstgang = 'Dienstgang',
  Gesetzlich = 'Gesetzlich',
  Gestempelt = 'Gestempelt',
  Manuell = 'Manuell',
  Rauchen = 'Rauchen',
  Schicht = 'Schicht',
}

export class Konflikt {
  constructor(readonly id: string, readonly konflikt: string) {}
}
export class Rechtshinweis {
  constructor(
    readonly name: string,
    readonly art: string,
    readonly backgroundcolor: BackgroundColor,
  ) {}
}

export enum BackgroundColor {
  Fatal = 'Fatal',
  Info = 'Info',
  Warnung = 'Warnung',
}

export class Abwesenheit {
  constructor(
    readonly id: string,
    readonly grund: Abwesenheitsgrund,
    readonly dauer: number,
  ) {}
}

export class Zuschlag {
  constructor(
    readonly dauer: number,
    readonly name: string,
    readonly prozent: number,
    readonly betrag: number = null,
  ) {}
}

export class ArbeitszeitAuditManager {
  constructor(
    readonly email: string,
    readonly vorname: string,
    readonly nachname: string,
  ) {}
}

export class ArbeitszeitAuditAenderung {
  constructor(
    readonly attribut: string,
    readonly nachher: string,
    readonly vorher: string,
  ) {}
}

export class Audit {
  constructor(
    readonly id: string,
    readonly typ: string,
    readonly aenderungsTyp: ArbeitszeitAuditAenderungsTyp,
    readonly aenderungsdatum: Date,
    readonly geaendertVon: string,
    readonly geaendertVonEmail: string,
    readonly manager: ArbeitszeitAuditManager,
    readonly aenderungen: ArbeitszeitAuditAenderung[],
  ) {}
}

export type antragsZeit = {
  time: Date;
  isChangeRequested: boolean;
};
export class Antrag {
  constructor(
    readonly id: string,
    readonly status: ArbeitszeitArbeitszeitantragStatus,
    readonly arbeitszeitId: string,
    readonly offen: boolean,
    readonly bemerkungAntragsteller: string,
    readonly bemerkungVorgesetzter: string,
    readonly datum: Date,
    readonly aenderungsdatum: Date,
    readonly erstellungsdatum: Date,
    readonly arbeitsbereich: Arbeitsbereich,
    readonly mitarbeiter: Mitarbeiter,
    readonly beginn?: antragsZeit,
    readonly ende?: antragsZeit,
  ) {}
}
